import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePrompts } from '../context/PromptContext';
import { useToast } from '../context/ToastContext';
import { useButtonState } from '../hooks/useButtonState';
import { format } from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEdit2, FiTrash2, FiCopy, FiCheck, FiAlertCircle, FiTag, FiMaximize2 } = FiIcons;

const PromptCard = ({ prompt, viewMode = 'grid', onView }) => {
  const { deletePrompt, selectedPrompts, togglePromptSelection } = usePrompts();
  const { success: showSuccess, error: showError } = useToast();
  const [copied, setCopied] = useState(false);
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [visibleTagsCount, setVisibleTagsCount] = useState(null);
  const tagsContainerRef = React.useRef(null);

  const {
    state: deleteState,
    error: deleteError,
    handleAction: handleDeleteAction,
    reset: resetDeleteState,
    icon: deleteIcon,
    classes: deleteClasses,
  } = useButtonState({
    idleText: 'Delete',
    loadingText: 'Deleting...',
    successText: 'Deleted!',
    failedText: 'Failed',
    idleIcon: FiTrash2,
    onSuccess: () => {
      showSuccess('Prompt deleted successfully!');
      setDeleteConfirm(false);
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to delete prompt';
      showError(errorMsg);
      setDeleteConfirm(false);
    },
    autoResetDelay: 2000,
  });

  // Generate a random pastel color based on ID for the header
  const getHeaderColor = (id) => {
    const colors = ['bg-neo-blue', 'bg-neo-green', 'bg-neo-purple', 'bg-neo-yellow', 'bg-neo-red'];
    const index = id.charCodeAt(id.length - 1) % colors.length;
    return colors[index];
  };

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(prompt.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      setTimeout(() => setDeleteConfirm(false), 3000);
    } else {
      handleDeleteAction(async () => {
        await deletePrompt(prompt.id);
      });
    }
  };

  const handleCheckboxChange = (e) => {
    e.preventDefault();
    e.stopPropagation();
    togglePromptSelection(prompt.id);
  };
  
  const handleViewClick = (e) => {
    // Prevent viewing if clicking functional buttons
    if (e.target.closest('button') || e.target.closest('a') || e.target.closest('.checkbox-area')) {
      return;
    }
    if (onView) onView(prompt);
  };

  const extractPlaceholders = (content) => {
    const matches = content.match(/\{[^}]+\}/g) || [];
    return [...new Set(matches)];
  };

  const truncateContent = (content, maxLength = 150) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const placeholders = extractPlaceholders(prompt.content);
  const isSelected = selectedPrompts.includes(prompt.id);

  React.useEffect(() => {
    if (!tagsContainerRef.current || viewMode !== 'grid') return;

    const updateVisibleTags = () => {
      const containerWidth = tagsContainerRef.current.offsetWidth;
      const availableWidth = containerWidth - 36;
      const averageTagWidth = 85;

      if (!prompt.tags || prompt.tags.length === 0) {
        setVisibleTagsCount(null);
        return;
      }

      const maxTags = Math.floor(availableWidth / averageTagWidth);
      setVisibleTagsCount(maxTags);
    };

    updateVisibleTags();

    const resizeObserver = new ResizeObserver(updateVisibleTags);
    resizeObserver.observe(tagsContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [prompt.tags, viewMode]);

  const getVisibleTags = () => {
    if (!prompt.tags || prompt.tags.length === 0) return [];
    if (tagsExpanded) return prompt.tags;
    if (visibleTagsCount === null) return prompt.tags.length > 1 ? [prompt.tags[0]] : prompt.tags;
    if (visibleTagsCount >= prompt.tags.length) return prompt.tags;
    return prompt.tags.slice(0, visibleTagsCount);
  };

  const visibleTags = getVisibleTags();
  const hiddenTagsCount = prompt.tags && prompt.tags.length > visibleTags.length ? prompt.tags.length - visibleTags.length : 0;

  // -- List View --
  if (viewMode === 'list') {
    return (
      <div 
        className={`neo-card transition-all duration-200 cursor-pointer group p-0 ${
          isSelected ? 'translate-x-[2px] translate-y-[2px] shadow-none ring-2 ring-black' : 'hover:-translate-y-1 hover:shadow-neo-lg'
        }`}
        onClick={handleViewClick}
      >
        <div className={`neo-window-header ${getHeaderColor(prompt.id)}`}>
          <div className="flex items-center justify-between w-full overflow-hidden">
           <span className="truncate text-sm font-bold uppercase mr-2">{prompt.title}</span>
              <div
                className="checkbox-area bg-white border-2 border-black min-w-[44px] min-h-[44px] w-11 h-11 md:min-w-[20px] md:min-h-[20px] md:w-5 md:h-5 flex-shrink-0 flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-all duration-200 md:hover:scale-110"
                onClick={handleCheckboxChange}
              >
                {isSelected && <div className="w-3 h-3 md:w-2.5 md:h-2.5 bg-black"></div>}
              </div>
          </div>
        </div>
        
        <div className="p-4 flex items-start justify-between bg-white group-hover:bg-gray-50 transition-colors">
          <div className="flex-1 min-w-0 pr-4">
            <p className="text-gray-800 font-mono text-sm leading-relaxed mb-3 line-clamp-2">
              {truncateContent(prompt.content, 200)}
            </p>
            <div className="flex items-center gap-1.5 mb-2 flex-wrap overflow-x-auto">
              {prompt.tags && prompt.tags.map((tag, i) => (
                <span key={i} className="px-3 py-1.5 border border-black text-xs font-bold bg-white uppercase min-w-[44px] min-h-[44px] md:px-2 md:py-1 md:min-w-auto md:min-h-[24px] flex items-center justify-center">
                  <SafeIcon icon={FiTag} size={10} className="mr-1" />
                  {tag}
                </span>
              ))}
            </div>
          </div>
          
          <div className="flex items-center space-x-2" onClick={e => e.stopPropagation()}>
            <button onClick={handleCopy} className="neo-btn-icon" title="Copy">
              <SafeIcon icon={copied ? FiCheck : FiCopy} size={16} />
            </button>
            <Link to={`/editor/${prompt.id}`} className="neo-btn-icon text-center" title="Edit">
              <SafeIcon icon={FiEdit2} size={16} />
            </Link>
            <button
              onClick={handleDelete}
              disabled={deleteState === 'loading'}
              className={`neo-btn-icon ${deleteState === 'loading' ? 'opacity-80 cursor-wait' : deleteConfirm ? 'bg-red-500 hover:bg-red-600' : 'hover:bg-red-100'} ${deleteClasses}`}
              title={deleteState === 'loading' ? 'Deleting...' : deleteConfirm ? 'Click again to confirm' : 'Delete'}
            >
              <SafeIcon icon={deleteState === 'loading' ? FiTrash2 : (deleteConfirm ? FiCheck : (deleteState === 'failed' ? FiAlertCircle : FiTrash2))} size={16} className={deleteState === 'loading' ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

      {/* Footer Status Bar */}
      <div className="bg-gray-100 border-t-2 border-black px-3 py-1 text-xs uppercase font-mono flex justify-between pointer-events-none">
        <span>Last Mod: {prompt.updated_at ? format(new Date(prompt.updated_at), 'yyyy-MM-dd') : 'Unknown'}</span>
      </div>
      </div>
    );
  }

  // -- Grid View --
  return (
    <div 
      className={`neo-card flex flex-col h-full transition-all duration-200 cursor-pointer group ${
        isSelected ? 'translate-x-[2px] translate-y-[2px] shadow-none ring-2 ring-black' : 'hover:-translate-y-1 hover:shadow-neo-lg'
      }`}
      onClick={handleViewClick}
    >
      {/* Window Header */}
      <div className={`neo-window-header ${getHeaderColor(prompt.id)}`}>
        <div className="flex items-center justify-between w-full overflow-hidden">
           <span className="truncate text-sm font-bold uppercase mr-2">{prompt.title}</span>
            <div
              className="checkbox-area bg-white border-2 border-black min-w-[44px] min-h-[44px] w-11 h-11 md:min-w-[20px] md:min-h-[20px] md:w-5 md:h-5 flex-shrink-0 flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-all duration-200 md:hover:scale-110"
              onClick={handleCheckboxChange}
            >
              {isSelected && <div className="w-3 h-3 md:w-2.5 md:h-2.5 bg-black"></div>}
            </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 flex-1 flex flex-col bg-white group-hover:bg-gray-50 transition-colors relative">
        {/* Overlay hint for click action */}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <SafeIcon icon={FiMaximize2} size={16} className="text-gray-400" />
        </div>

        <div className="flex-1 mb-4 pointer-events-none">
          <p className="text-gray-800 font-mono text-sm leading-relaxed break-words line-clamp-4">
            {truncateContent(prompt.content)}
          </p>
        </div>

        {placeholders.length > 0 && (
          <div className="mb-4 pt-3 border-t-2 border-dashed border-gray-300 pointer-events-none">
            <div className="flex flex-wrap gap-2">
              {placeholders.slice(0, 3).map((placeholder, index) => (
                <span key={index} className="placeholder-highlight text-xs cursor-default">
                  {placeholder}
                </span>
              ))}
              {placeholders.length > 3 && (
                <span className="text-xs font-bold border border-black px-1 cursor-default">
                  +{placeholders.length - 3}
                </span>
              )}
            </div>
          </div>
        )}

        <div className="mt-auto pt-3 border-t-2 border-black flex flex-col gap-3" onClick={e => e.stopPropagation()}>
           {/* Tags Section */}
          <div className="flex items-start gap-2 overflow-x-auto">
            <SafeIcon icon={FiTag} size={14} className="mt-0.5 flex-shrink-0" />
              <div ref={tagsContainerRef} className="flex flex-wrap gap-1 w-full min-w-0">
                {visibleTags.map((tag, i) => (
                    <span key={i} className="px-3 py-1.5 bg-gray-100 border border-black text-xs font-bold uppercase min-w-[44px] min-h-[44px] md:px-2 md:py-0.5 md:min-w-auto md:min-h-[24px] flex items-center justify-center">
                      {tag}
                    </span>
                  ))}

                  {hiddenTagsCount > 0 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setTagsExpanded(!tagsExpanded); }}
                      className="px-3 py-1.5 bg-black text-white border border-black text-xs font-bold hover:bg-gray-800 transition-colors min-w-[44px] min-h-[44px] md:px-2 md:py-1 md:min-w-auto md:min-h-[24px] flex items-center justify-center"
                    >
                      {tagsExpanded ? '-' : `+${hiddenTagsCount}`}
                    </button>
                  )}
                {(!prompt.tags || prompt.tags.length === 0) && (
                  <span className="text-xs text-gray-400 font-mono">NO TAGS</span>
                )}
            </div>
          </div>
          
           {/* Actions Section */}
            <div className="flex items-center justify-end space-x-2">
              <button
                onClick={handleCopy}
                className="p-3 min-w-[44px] min-h-[44px] md:p-2 md:min-w-auto md:min-h-auto border-2 border-black bg-white hover:bg-neo-green transition-colors shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-x-[1px] active:translate-y-[1px] flex items-center justify-center"
                title="Copy"
              >
                <SafeIcon icon={copied ? FiCheck : FiCopy} size={16} />
              </button>
              <Link
                to={`/editor/${prompt.id}`}
                className="p-3 min-w-[44px] min-h-[44px] md:p-2 md:min-w-auto md:min-h-auto border-2 border-black bg-white hover:bg-neo-yellow transition-colors shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-x-[1px] active:translate-y-[1px] inline-flex items-center justify-center"
                title="Edit"
              >
                <SafeIcon icon={FiEdit2} size={16} />
              </Link>
              <button
                onClick={handleDelete}
                disabled={deleteState === 'loading'}
                className={`p-3 min-w-[44px] min-h-[44px] md:p-2 md:min-w-auto md:min-h-auto border-2 border-black transition-colors shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-x-[1px] active:translate-y-[1px] flex items-center justify-center ${deleteState === 'loading' ? 'opacity-80 cursor-wait' : (deleteConfirm ? 'bg-red-500 hover:bg-red-600' : 'bg-white hover:bg-neo-red')} ${deleteClasses}`}
                title={deleteState === 'loading' ? 'Deleting...' : deleteConfirm ? 'Click again to confirm' : 'Delete'}
              >
                <SafeIcon icon={deleteState === 'loading' ? FiTrash2 : (deleteConfirm ? FiCheck : (deleteState === 'failed' ? FiAlertCircle : FiTrash2))} size={16} className={deleteState === 'loading' ? 'animate-spin' : ''} />
              </button>
            </div>
        </div>
      </div>

      {/* Footer Status Bar */}
      <div className="bg-gray-100 border-t-2 border-black px-3 py-1 text-xs uppercase font-mono flex justify-between pointer-events-none">
        <span>Last Mod: {prompt.updated_at ? format(new Date(prompt.updated_at), 'yyyy-MM-dd') : 'Unknown'}</span>
      </div>
    </div>
  );
};

export default PromptCard;